import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  DeleteProgramDefinitionOptions,
  GetProgramDefinitionByExternalIdAndNamespaceOptions,
  GetProgramDefinitionByExternalIdAndNamespaceResponse,
  ProgramDefinition,
  ProgramDefinitionQuery,
  ProgramDefinitionsQueryBuilder,
  QueryProgramDefinitionsResponse,
  UpdateProgramDefinition,
  createProgramDefinition as universalCreateProgramDefinition,
  deleteProgramDefinition as universalDeleteProgramDefinition,
  getProgramDefinition as universalGetProgramDefinition,
  getProgramDefinitionByExternalIdAndNamespace as universalGetProgramDefinitionByExternalIdAndNamespace,
  queryProgramDefinitions as universalQueryProgramDefinitions,
  typedQueryProgramDefinitions as universalTypedQueryProgramDefinitions,
  updateProgramDefinition as universalUpdateProgramDefinition,
} from './benefit-programs-v1-program-definition-program-definitions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/benefit-programs' };

export function createProgramDefinition(
  httpClient: HttpClient
): CreateProgramDefinitionSignature {
  return (
    programDefinition: NonNullablePaths<
      ProgramDefinition,
      `externalId` | `namespace`,
      2
    >
  ) =>
    universalCreateProgramDefinition(
      programDefinition,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateProgramDefinitionSignature {
  /**
   * Creates a program definition.
   * @param - Program definition to create.
   * @returns Created program definition.
   */
  (
    programDefinition: NonNullablePaths<
      ProgramDefinition,
      `externalId` | `namespace`,
      2
    >
  ): Promise<ProgramDefinition>;
}

export function updateProgramDefinition(
  httpClient: HttpClient
): UpdateProgramDefinitionSignature {
  return (
    _id: string,
    programDefinition: NonNullablePaths<UpdateProgramDefinition, `revision`, 2>
  ) =>
    universalUpdateProgramDefinition(
      _id,
      programDefinition,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateProgramDefinitionSignature {
  /**
   * Updates a program definition.
   *
   * Each time the program definition is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the program definition.
   * This ensures you're working with the latest program definition
   * and prevents unintended overwrites.
   * @param - Program definition ID.
   * @returns Updated program definition.
   */
  (
    _id: string,
    programDefinition: NonNullablePaths<UpdateProgramDefinition, `revision`, 2>
  ): Promise<ProgramDefinition>;
}

export function deleteProgramDefinition(
  httpClient: HttpClient
): DeleteProgramDefinitionSignature {
  return (
    programDefinitionId: string,
    options?: DeleteProgramDefinitionOptions
  ) =>
    universalDeleteProgramDefinition(
      programDefinitionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteProgramDefinitionSignature {
  /**
   * Deletes a program definition.
   * @param - ID of the program definition to delete.
   */
  (
    programDefinitionId: string,
    options?: DeleteProgramDefinitionOptions
  ): Promise<void>;
}

export function getProgramDefinition(
  httpClient: HttpClient
): GetProgramDefinitionSignature {
  return (programDefinitionId: string) =>
    universalGetProgramDefinition(
      programDefinitionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetProgramDefinitionSignature {
  /**
   * Retrieves a program definition.
   * @param - ID of the program definition to retrieve.
   * @returns Retrieved program definition.
   */
  (programDefinitionId: string): Promise<ProgramDefinition>;
}

export function getProgramDefinitionByExternalIdAndNamespace(
  httpClient: HttpClient
): GetProgramDefinitionByExternalIdAndNamespaceSignature {
  return (
    namespace: string,
    options: NonNullablePaths<
      GetProgramDefinitionByExternalIdAndNamespaceOptions,
      `externalId`,
      2
    >
  ) =>
    universalGetProgramDefinitionByExternalIdAndNamespace(
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetProgramDefinitionByExternalIdAndNamespaceSignature {
  /**
   * Retrieves a program definition with the specified external ID and namespace.
   * @param - Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   */
  (
    namespace: string,
    options: NonNullablePaths<
      GetProgramDefinitionByExternalIdAndNamespaceOptions,
      `externalId`,
      2
    >
  ): Promise<GetProgramDefinitionByExternalIdAndNamespaceResponse>;
}

export function queryProgramDefinitions(
  httpClient: HttpClient
): QueryProgramDefinitionsSignature {
  return () =>
    universalQueryProgramDefinitions(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryProgramDefinitionsSignature {
  /**
   * Creates a query to retrieve a list of program definitions.
   *
   * The Query Program Definitions method builds a query to retrieve a list of program definitions and returns a `ProgramDefinitionsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `ProgramDefinitionsQueryBuilder` methods onto the query. `ProgramDefinitionsQueryBuilder` methods enable you to filter, sort, and control the results that Query Program Definitions returns.
   *
   * Query Program Definitions has a default paging limit of 50, which you can override.
   *
   *  For a full description of the item object, see the object returned for the `items` property in `ProgramDefinitionsQueryResult`.
   */
  (): ProgramDefinitionsQueryBuilder;
}

export function typedQueryProgramDefinitions(
  httpClient: HttpClient
): TypedQueryProgramDefinitionsSignature {
  return (query: ProgramDefinitionQuery) =>
    universalTypedQueryProgramDefinitions(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryProgramDefinitionsSignature {
  /** */
  (query: ProgramDefinitionQuery): Promise<
    NonNullablePaths<QueryProgramDefinitionsResponse, `programDefinitions`, 2>
  >;
}

export {
  AccountInfo,
  ActionEvent,
  Cascade,
  CreateProgramDefinitionRequest,
  CreateProgramDefinitionResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteProgramDefinitionOptions,
  DeleteProgramDefinitionRequest,
  DeleteProgramDefinitionResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetOrCreateProgramDefinitionRequest,
  GetOrCreateProgramDefinitionResponse,
  GetProgramDefinitionByExternalIdAndNamespaceOptions,
  GetProgramDefinitionByExternalIdAndNamespaceRequest,
  GetProgramDefinitionByExternalIdAndNamespaceResponse,
  GetProgramDefinitionRequest,
  GetProgramDefinitionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  ProgramDefinition,
  ProgramDefinitionQuerySpec,
  ProgramDefinitionTranslated,
  ProgramDefinitionTranslationDeleted,
  ProgramDefinitionsQueryBuilder,
  ProgramDefinitionsQueryResult,
  QueryProgramDefinitionsRequest,
  QueryProgramDefinitionsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  UpdateProgramDefinition,
  UpdateProgramDefinitionRequest,
  UpdateProgramDefinitionResponse,
  UpsertProgramDefinitionRequest,
  UpsertProgramDefinitionResponse,
  WebhookIdentityType,
  utils,
} from './benefit-programs-v1-program-definition-program-definitions.universal.js';
